import React, { useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { store } from 'components/Store';
import time from 'utils/time';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import useTitleScrolledStyles from 'components/Dashboard/hooks/useTitleScrolledStyles';
import ThumbsDown from 'components/Common/ThumbsDown';
import Share from 'components/Dashboard/components/Share';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import TextSelector from 'components/Common/TextSelector';
import normalize from 'components/Dashboard/utilities/normalizeString';

const RegulationCommentContentPage = ({ currentNew, contentContainer, hideSummary, scrollbarRef, scrolling, reportWidget }) => {
  const { dispatch } = useContext(store);
  const { createMarkup } = useCreateMarkup();
  const titleSection = useRef(null);
  const { showFixedTitle, titleStyles } = useTitleScrolledStyles({ contentContainer, titleSection, leftValue: 0 });
  const { id, title, objectId, objectType, canonicalUrl } = currentNew ?? {};
  const item = {
    id,
    title,
    objectID: objectId,
    type: objectType,
  };

  const commentContentRef = useRef(null);

  const documentTitle = currentNew?.regulationDocumentTitle ?? currentNew?.documentTitle;

  if (reportWidget) {
    return (
      <div className='p-3 p-lg-4'>
        <p className='heading-date mb-0'>
          {!!currentNew?.submitterName && (
            <>
              {currentNew.submitterName}
              <span className='dot'></span>
            </>
          )}
          {!!currentNew?.postedDate && (
            <span className='date'>{time(currentNew.postedDate).formatGeneralDate()}</span>
          )}
          {!!currentNew?.sourceUrl && (
            <>
              <span className='dot' />
              <a className='mb-0' href={currentNew.sourceUrl} target='_blank' rel='noopener noreferrer'>
                Source: {new URL(currentNew.sourceUrl).hostname}
              </a>
            </>
          )}
        </p>
        {!!currentNew?.body && (
          <div className='pt-3 news-content-in parliamentary-content-in context committee-publication-content'>
            <div className='explanatory-container' dangerouslySetInnerHTML={createMarkup(currentNew.body)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className='section-title pb-2 regulations-content-title p-3 p-lg-4' ref={titleSection}>
        <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(currentNew?.title)} />
        <div className='row mx-0 justify-content-between align-items-center'>
          <p className='heading-date mb-0'>
            {!!currentNew?.submitterName && (
              <>
                {currentNew.submitterName}
                <span className='dot'></span>
              </>
            )}
            {!!currentNew?.organization && (
              <>
                {currentNew.organization}
                <span className='dot'></span>
              </>
            )}
            {!!currentNew?.postedDate && (
              <span className='date'>{time(currentNew.postedDate).formatGeneralDate()}</span>
            )}
            {!!currentNew?.sourceUrl && (
              <>
                <span className='dot' />
                <a
                  className='legislation-source mb-0'
                  href={currentNew.sourceUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Go to source
                </a>
              </>
            )}
          </p>
          <div className='flex-centered'>
            <ThumbsDown
              item={{ objectID: currentNew?.objectId }}
              nameSection='RegulationComments'
              show={!!currentNew?.keywordsFromLists}
              contentPage={true}
              alreadyMarked={!!currentNew?.feedback}
              keywordsFromLists={currentNew?.keywordsFromLists}
              thick={true}
            />
            <Share
              item={item}
              id={id}
              title={title}
              link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
            />
          </div>
        </div>
        <div className='pt-3'>
          {currentNew?.regulationDocketId ? (
            <Link
              to={`/regulation-docket/${currentNew.regulationDocketId}/${normalize(currentNew.docketTitle)}`}
              className='general-button hovered-button border rounded-pill px-3 py-1 mr-3 d-inline-block reset-link link-main-blue'
              onClick={() => dispatch({ type: 'MODIFY_SECTION', parameter: 'clickedOnContentUrl', value: null })}
            >
              Go to docket
            </Link>
          ) : (
            <button className='general-button hovered-button border rounded-pill px-3 py-1 mr-3' disabled>
              Go to docket
            </button>
          )}
          {currentNew?.regulationDocumentId ? (
            <Link
              to={`/regulation-document/${currentNew.regulationDocumentId}/${normalize(documentTitle)}#comments`}
              className='general-button hovered-button border rounded-pill px-3 py-1 mr-3 d-inline-block reset-link link-main-blue'
              onClick={() => dispatch({ type: 'MODIFY_SECTION', parameter: 'clickedOnContentUrl', value: null })}
            >
              Go to document
            </Link>
          ) : (
            <button className='general-button hovered-button border rounded-pill px-3 py-1 mr-3' disabled>
              Go to document
            </button>
          )}
        </div>
      </div>
      <div
        className={`content-item-scrolled-title main-sticky section-title bg-white parliamentary-scrolled-title ${
          showFixedTitle ? 'item-scrolled-title-visible' : ''
        }`}
        style={titleStyles}
      >
        <div className='d-flex justify-content-between scrolled-title-container'>
          <h3
            className='main-title news-title bg-white content-item-title'
            dangerouslySetInnerHTML={createMarkup(currentNew?.title)}
          />
          <ThumbsDown
            item={{ objectID: currentNew?.objectId }}
            nameSection='RegulationComments'
            show={!!currentNew?.keywordsFromLists}
            contentPage={true}
            alreadyMarked={!!currentNew?.feedback}
            keywordsFromLists={currentNew?.keywordsFromLists}
            thick={true}
          />
          <Share
            item={item}
            id={id}
            title={title}
            link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
          />
        </div>
      </div>
      <ContentSummary content={currentNew} hide={hideSummary} />
      <div className='border-bottom px-3 px-lg-4 pb-3 pb-lg-4'>
        {currentNew?.govCommentId && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Comment ID</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>
              {currentNew?.govCommentId}
            </span>
          </div>
        )}
        {currentNew?.submitterName && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Submitter</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>
              {currentNew?.submitterName}
            </span>
          </div>
        )}

        {currentNew?.agency && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Agency</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>{currentNew?.agency}</span>
          </div>
        )}
        {currentNew?.docketType && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Docket Type</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>
              {currentNew?.docketType}
            </span>
          </div>
        )}
        {currentNew?.docketTitle && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Docket title</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>
              {currentNew?.docketTitle}
            </span>
          </div>
        )}
        {currentNew?.rin && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>RIN</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>{currentNew?.rin}</span>
          </div>
        )}
        {currentNew?.postedDate && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Posted date</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>
              {time(currentNew?.postedDate).formatGeneralDate()}
            </span>
          </div>
        )}
        {currentNew?.sourceUrl && (
          <div className='d-flex align-items-center mb-2'>
            <span className='d-inline-block flex-grow-1 col-4 paragraph-p1 text-main-blue-65 px-0'>Source URL</span>
            <span className='d-inline-block flex-grow-1 col-12 paragraph-p1 text-main-blue'>
              <a
                href={currentNew?.sourceUrl}
                className='text-light-blue link-hover'
                target='_blank'
                rel='noopener noreferrer'
              >
                {currentNew?.sourceUrl}
              </a>
            </span>
          </div>
        )}
      </div>
      <div className='px-3 px-lg-4 pb-3 pb-lg-4'>
        {!!currentNew?.body && (
          <TextSelector currentNew={currentNew} scrollbarRef={scrollbarRef} scrolling={scrolling}>
            <div className='pt-3 news-content-in parliamentary-content-in context committee-publication-content'>
              <div
                className='explanatory-container'
                dangerouslySetInnerHTML={createMarkup(currentNew.body)}
                ref={commentContentRef}
              />
            </div>
          </TextSelector>
        )}
      </div>
    </>
  );
};

export default RegulationCommentContentPage;
